import RequestHandler from "util/RequestHandler";

export const SAVED_FILTER_PAGE_KEYS = {
  TenantAccountList: "TenantAccountList",
  AccountTransactionList: "AccountTransactionList",
  ConsolidatedTenantAccountList: "ConsolidatedTenantAccountList",
  ConsolidatedAccountTransactionList: "ConsolidatedAccountTransactionList",
};

/**
 * Checks if a form value counts as a filled filter selection.
 */
export const isFilterValueFilled = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    // moment / dayjs range or similar
    if (value._isAMomentObject || typeof value.isValid === "function") return true;
    return Object.keys(value).length > 0;
  }
  return true;
};

/**
 * Returns true when at least one form field (excluding ignored keys) has a value.
 */
export const hasFilterSelection = (values, ignoredKeys = []) => {
  if (!values) return false;
  return Object.keys(values).some((key) => {
    if (ignoredKeys.includes(key)) return false;
    return isFilterValueFilled(values[key]);
  });
};

/**
 * Builds OData OR conditions for multi-select exact equality.
 * Example: (iban eq 'a' or iban eq 'b')
 */
export const buildMultiEqCondition = (fieldName, values, quote = true) => {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  if (list.length === 0) return null;
  const conditions = list.map((v) =>
    quote ? `${fieldName} eq '${String(v).replaceAll("'", "''")}'` : `${fieldName} eq ${v}`
  );
  return `(${conditions.join(" or ")})`;
};

/**
 * Builds OData OR of contains conditions for multi-select text fields.
 */
export const buildMultiContainsCondition = (odataHelper, fieldName, values) => {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  if (list.length === 0) return null;
  const conditions = list.map((v) => odataHelper.contains(fieldName, v));
  return `(${conditions.join(" or ")})`;
};

/**
 * Builds account-number style OR (contains on accountNumber OR accountNumberWithoutSuffix) for each selected value.
 */
export const buildMultiAccountNumberCondition = (odataHelper, values) => {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  if (list.length === 0) return null;
  const conditions = list.map(
    (v) =>
      "(" +
      odataHelper.contains("accountNumber", v) +
      " or " +
      odataHelper.contains("accountNumberWithoutSuffix", v) +
      ")"
  );
  return `(${conditions.join(" or ")})`;
};

/**
 * Builds account-transaction account-number style OR for each selected value.
 */
export const buildMultiTenantAccountNumberCondition = (odataHelper, values) => {
  const list = Array.isArray(values) ? values : values ? [values] : [];
  if (list.length === 0) return null;
  const conditions = list.map(
    (v) =>
      "(" +
      odataHelper.contains("ibanOrAccountNumber", v) +
      " or " +
      odataHelper.contains("tenantAccountNumberWithSuffix", v) +
      ")"
  );
  return `(${conditions.join(" or ")})`;
};

export const hasUrlFilterParams = () => {
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get("filter") || params.get("bankId"));
};

/**
 * Converts form values into a JSON-serializable object.
 * Date range fields become transactionDateMin / transactionDateMax ISO strings.
 */
export const serializeFilterValues = (values) => {
  if (!values) return {};
  const result = { ...values };

  if (result.transactionDateRange && Array.isArray(result.transactionDateRange)) {
    const [min, max] = result.transactionDateRange;
    if (min) {
      result.transactionDateMin =
        typeof min.toISOString === "function" ? min.toISOString() : min;
    }
    if (max) {
      result.transactionDateMax =
        typeof max.toISOString === "function" ? max.toISOString() : max;
    }
    delete result.transactionDateRange;
  }

  // Drop Ant Design internal / non-serializable leftovers
  Object.keys(result).forEach((key) => {
    const v = result[key];
    if (v && typeof v === "object" && !Array.isArray(v) && v._isAMomentObject) {
      result[key] = v.toISOString();
    }
  });

  return result;
};

export const getSavedFilter = (pageKey) => {
  return RequestHandler.get(
    `/TenantUserSavedFilter/Get?pageKey=${encodeURIComponent(pageKey)}`
  ).then((response) => {
    if (!response || !response.success || !response.data || !response.data.filterJson) {
      return null;
    }
    try {
      return JSON.parse(response.data.filterJson);
    } catch (e) {
      return null;
    }
  });
};

export const saveFilter = (pageKey, payload) => {
  return RequestHandler.post("/TenantUserSavedFilter/Save", {
    PageKey: pageKey,
    FilterJson: JSON.stringify(payload),
  });
};

export const deleteSavedFilter = (pageKey) => {
  return RequestHandler.delete(
    `/TenantUserSavedFilter/Delete?pageKey=${encodeURIComponent(pageKey)}`
  );
};
